
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { UserPlus } from 'lucide-react';

interface PersonalInfoTabProps {
  userId: string;
  profile: any;
}

const PersonalInfoTab: React.FC<PersonalInfoTabProps> = ({ userId, profile }) => {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [formData, setFormData] = useState({
    firstName: profile?.first_name || '',
    lastName: profile?.last_name || '',
    headline: '',
    summary: '',
    location: '',
    currentPosition: '',
    yearsOfExperience: '',
    githubUrl: '',
    linkedinUrl: '',
  });

  useEffect(() => {
    if (userId) {
      fetchJobSeekerProfile();
    }
  }, [userId]);

  const fetchJobSeekerProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('job_seeker_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching job seeker profile:', error.message);
        return;
      }

      if (data) {
        setFormData({
          firstName: profile?.first_name || '',
          lastName: profile?.last_name || '',
          headline: data.headline || '',
          summary: data.summary || '',
          location: data.location || '',
          currentPosition: data.current_position || '',
          yearsOfExperience: data.years_of_experience ? String(data.years_of_experience) : '',
          githubUrl: data.github_url || '',
          linkedinUrl: data.linkedin_url || '',
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    try {
      if (!userId) {
        toast({
          title: "Error",
          description: "User not authenticated",
          variant: "destructive",
        });
        return;
      }

      let completedFields = 0;
      let totalFields = 9; // Increased for new fields
      if (formData.firstName) completedFields++;
      if (formData.lastName) completedFields++;
      if (formData.headline) completedFields++;
      if (formData.summary) completedFields++;
      if (formData.location) completedFields++;
      if (formData.currentPosition) completedFields++;
      if (formData.yearsOfExperience) completedFields++;
      if (formData.githubUrl) completedFields++;
      if (formData.linkedinUrl) completedFields++;

      const profileCompletion = Math.round((completedFields / totalFields) * 100);

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: formData.firstName,
          last_name: formData.lastName,
        })
        .eq('id', userId);

      if (profileError) throw profileError;

      const { error: jobSeekerProfileError } = await supabase
        .from('job_seeker_profiles')
        .upsert({
          id: userId,
          headline: formData.headline,
          summary: formData.summary,
          location: formData.location,
          current_position: formData.currentPosition,
          years_of_experience: parseInt(formData.yearsOfExperience) || 0,
          github_url: formData.githubUrl,
          linkedin_url: formData.linkedinUrl,
          profile_completion: profileCompletion,
        });

      if (jobSeekerProfileError) throw jobSeekerProfileError;

      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
      });
    } catch (error) {
      console.error('Error updating profile:', error.message);
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-[#0EA5E9]" />
          Personal Information
        </CardTitle>
        <CardDescription>
          Update your personal information and profile details
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="border-[#0EA5E9]/30"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="border-[#0EA5E9]/30"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="headline">Professional Headline</Label>
            <Input
              id="headline"
              name="headline"
              placeholder="e.g., Senior Software Engineer"
              value={formData.headline}
              onChange={handleInputChange}
              className="border-[#0EA5E9]/30"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="summary">Professional Summary</Label>
            <Textarea
              id="summary"
              name="summary"
              placeholder="Write a brief summary about your professional background"
              value={formData.summary}
              onChange={handleInputChange}
              className="border-[#0EA5E9]/30 min-h-[100px]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                name="location"
                placeholder="e.g., San Francisco, CA"
                value={formData.location}
                onChange={handleInputChange}
                className="border-[#0EA5E9]/30"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currentPosition">Current Position</Label>
              <Input
                id="currentPosition"
                name="currentPosition"
                placeholder="e.g., Software Engineer at Google"
                value={formData.currentPosition}
                onChange={handleInputChange}
                className="border-[#0EA5E9]/30"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="yearsOfExperience">Years of Experience</Label>
            <Input
              id="yearsOfExperience"
              name="yearsOfExperience"
              type="number"
              placeholder="e.g., 5"
              value={formData.yearsOfExperience}
              onChange={handleInputChange}
              className="border-[#0EA5E9]/30"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="githubUrl">GitHub URL</Label>
              <Input
                id="githubUrl"
                name="githubUrl"
                type="url"
                placeholder="e.g., https://github.com/yourusername"
                value={formData.githubUrl}
                onChange={handleInputChange}
                className="border-[#0EA5E9]/30"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
              <Input
                id="linkedinUrl"
                name="linkedinUrl"
                type="url"
                placeholder="e.g., https://linkedin.com/in/yourusername"
                value={formData.linkedinUrl}
                onChange={handleInputChange}
                className="border-[#0EA5E9]/30"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90 w-full"
            disabled={isUpdating}
          >
            {isUpdating ? 'Updating...' : 'Update Profile'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default PersonalInfoTab;
